// all new
import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
// components
import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import SvgColor from 'src/components/svg-color';
import StarIcon from '@mui/icons-material/Star';

// redux
import { useSelector } from 'src/redux/store';
import { GridCheckCircleIcon } from '@mui/x-data-grid';


// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  setting: icon('ic_setting'),
  admin: icon('ic_admin'),
  newinvoice: icon('ic_invoice-add'),
  grnBill: icon('ic_bill-check'),
  purchaseOrder: icon('ic_purchase'),
  price: icon('ic_price'),
  stock: icon('ic_stock'),
  credit: icon('ic_credit'),
  receable: icon('ic_receive'),
  payable: icon('ic_payable'),
  cheque: icon('ic_cheque'),
  cash: icon('ic_cash'),
  report: icon('ic_report'),
  reportbar: icon('ic_reportbar'),
  configuration: icon('ic_configuration'),
  setups: icon('ic_setup'),
  pay: icon('ic_pay'),
  bill: icon('ic_bill'),
  customerInvoice: icon('ic_customerInvoice'),
  salereceipts: icon('ic_salereceipts'),
  manufacturing: icon('ic_manufact'),
  bank: icon('ic_bank'),
  money: icon('ic_money'),
  bankReceipt: icon('ic_bankReceipts'),
  stockAdjustment: icon('ic_stockAdjustmet'),
  stockReplenishment: icon('ic_stockReplenishment'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();
  const userRole = useSelector((state) => state.auth.user.roles[0]);
  const loginLocation = useSelector((state) => state.auth.user);

  const data = useMemo(
    () => [
      // OVERVIEW
      // ----------------------------------------------------------------------
      {
        subheader: userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_STORES_INCHARGE" ? t('ERP') : null,

        items: [
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_STORES_INCHARGE" ? { title: t('dashboard'), path: paths.dashboard.root, icon: ICONS.dashboard } : null
        ].filter(Boolean),
      },

      // RETAIL SALES
      // ----------------------------------------------------------------------
      {
        subheader: t('Retail Sales'),
        items: [
          // POS
          userRole === "ROLE_ADMIN" || userRole === "ROLE_POS" ? {
            title: t('POS'),
            path: paths.dashboard.possecondview,
            icon: ICONS.ecommerce,
          } : null,

          // POS Receivable
          userRole === "ROLE_ADMIN" || userRole === "ROLE_POS" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('POS Receivable'),
            path: paths.dashboard.posReceivable.root,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('POS receivable'), path: paths.dashboard.posReceivable.root },
              { title: t('POS Received List'), path: paths.dashboard.cashReceivable.receivedList },
            ],
          } : null,
          // Measurement
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_POS" || userRole === "ROLE_IRON_PACKING_DELIVERY" || userRole === "ROLE_STORES_INCHARGE" ? {
            title: t('Measurement'),
            path: paths.dashboard.measurement.root,
            icon: ICONS.ecommerce,
            children: [
              userRole === "ROLE_ADMIN" || userRole === "ROLE_POS" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_STORES_INCHARGE" ? { title: t('Create Measurement'), path: paths.dashboard.measurement.new } : null,
              userRole === "ROLE_ADMIN" || userRole === "ROLE_POS" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_IRON_PACKING_DELIVERY" || userRole === "ROLE_STORES_INCHARGE" ? { title: t('Measurement List'), path: paths.dashboard.measurement.root } : null,
              userRole === "ROLE_ADMIN" || userRole === "ROLE_POS" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_STORES_INCHARGE" ? { title: t('Measurement Pending List'), path: paths.dashboard.pos.measurementPending } : null,
            ].filter(item => item !== null),
          } : null,

          // Trial Confirmation
          {
            title: t('Trial Confirmation'),
            path: paths.dashboard.trialConfirmation.confirmedlist,
            icon: ICONS.ecommerce,
            children: [
              { title: t('Trial Confirmation'), path: paths.dashboard.trialConfirmation.root },
              { title: t('Trial Confirmed List'), path: paths.dashboard.trialConfirmation.confirmedlist },
            ],
          },

          // Boutique Received
          {
            title: t('Boutique Received'),
            path: paths.dashboard?.production?.boutiqueRecivedRoot,
            icon: ICONS.stock,
            children: [
              {
                title: t('Boutique Received'),
                path: paths.dashboard?.production?.boutiqueRecivedCreatePage,
              },
              {
                title: t('Boutique Received List'),
                path: paths.dashboard?.production?.boutiqueRecivedListPage,
              }
            ]
          },

          // Credit Note
          userRole === "ROLE_ADMIN" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('Credit Note'),
            path: paths.dashboard.pos.root,
            icon: ICONS.ecommerce,
            children: [
              { title: t('Credit Note'), path: paths.dashboard.pos.creditNote },
              { title: t('Credit Note List'), path: paths.dashboard.pos.creditNoteList },
            ],
          } : null,

          // Sales Discount History
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('sales Discount History'),
            path: paths.dashboard.pos.discountHistory,
            icon: ICONS.ecommerce,
          } : null,

          // Price Management
          userRole === "ROLE_ADMIN" ? {
            title: t('Price Management'),
            path: paths.dashboard.priceManagement.root,
            icon: ICONS.setting,
            children: [
              { title: t('Update New Price'), path: paths.dashboard.priceManagement.new },
              { title: t('Price History List'), path: paths.dashboard.priceManagement.list },
            ],
          } : null,

          // Corporate Discount
          userRole === "ROLE_ADMIN" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('Corporate Discount'),
            path: paths.dashboard.corporateDiscount.root,
            icon: ICONS.setting,
            children: [
              { title: t('Create Corporates'), path: paths.dashboard.corporateDiscount.new },
              { title: t('Define Discount Schemas'), path: paths.dashboard.corporateDiscount.defineDiscount },
              {
                title: t('Corporate Reports'),
                path: paths.dashboard.root,
                children: [
                  {
                    title: t('Discount Wise'),
                    path: paths.dashboard.corporateDiscount.discountWise,
                  },
                  {
                    title: t('Corporate Wise'),
                    path: paths.dashboard.corporateDiscount.corporateWise,
                  },
                  {
                    title: t('Item Groupe Wise'),
                    path: paths.dashboard.corporateDiscount.itemGroupWise,
                  },
                  {
                    title: t('Item Wise'),
                    path: paths.dashboard.corporateDiscount.itemWise,
                  },
                ],
              },
            ],
          } : null,

          // Sales Report
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_FIN_ASSISTANT" || userRole === "ROLE_POS" || userRole==="ROLE_STORES_INCHARGE"? {
            title: t('Sales Report'),
            path: paths.dashboard.report.itemwise,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('Item Wise'), path: paths.dashboard.report.itemwise },
              { title: t('Item Group Wise'), path: paths.dashboard.report.list },
              { title: t('Customer Wise'), path: paths.dashboard.report.customerwise },
              { title: t('Sales Summary - Branch'), path: paths.dashboard.report.salesSummary },
              { title: t('Invoice Summery Report'), path: paths.dashboard.report.locationdetailsSalesSummary },
              { title: t('sales History'), path: paths.dashboard.pos.history },
            ],
          } : null,

          // Collection Report
          userRole === "ROLE_ADMIN" || userRole === "ROLE_POS" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('Collection Report'),
            path: paths.dashboard.stockMovement.root,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('Daily Collection'), path: paths.dashboard.report.collection },
              { title: t('Cash Collection report'), path: paths.dashboard.sales.root },
              // { title: t('POS receivable'), path: paths.dashboard.posReceivable.root },
              // { title: t('POS Received List'), path: paths.dashboard.cashReceivable.receivedList },
            ],
          } : null,

        ].filter(item => item !== null),
      },

      // STORES & PROCUREMENT
      // ----------------------------------------------------------------------
      {
        subheader: t('Stores & Procurement'),
        items: [
          // Material Master
          userRole === "ROLE_ADMIN" || userRole === "ROLE_STORES_INCHARGE" ? {
            title: t('Material Master'),
            path: paths.dashboard.materialmaster.root,
            icon: ICONS.setting,
            children: [
              { title: t('Master Data'), path: paths.dashboard.materialmaster.new },
              { title: t('Master Data List'), path: paths.dashboard.materialmaster.newlist },
              { title: t('Create Product Category'), path: paths.dashboard.category.new },
            ],
          } : null,

          // Stock Issuing
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_STORES_INCHARGE" ? {
            title: t('Stock Issuing'),
            path: paths.dashboard.stockissuing.root,
            icon: ICONS.ecommerce,
            children: [
              { title: t('Stock Issues - Pending'), path: paths.dashboard.stockissuing.root },
              { title: t('Stock Issued List'), path: paths.dashboard.stockissuing.issuedList },
            ],
          } : null,

          // Additional Material
          userRole === "ROLE_ADMIN" || userRole === "ROLE_STORES_INCHARGE" ? {
            title: t('Additional material'),
            path: paths.dashboard.stockAdjustment.root,
            icon: ICONS.stockAdjustment,
            children: [
              ...(userRole === "ROLE_ADMIN"
                ? [{ title: t('Create Stock Type'), path: paths.dashboard.stockAdjustment.Additional_material }]
                : []
              ),
              { title: t('Create Additional Material'), path: paths.dashboard.additional_material.add },
            ],
          } : null,

          // Purchase Order
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_STORES_INCHARGE" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('purchase order'),
            path: paths.dashboard.po.root,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('PO creation'), path: paths.dashboard.po.new },
              { title: t('PO list'), path: paths.dashboard.po.root },
              { title: t('Service PO creation'), path: paths.dashboard.servicePO.new },
              { title: t('Service PO list'), path: paths.dashboard.servicePO.root },
            ],
          } : null,

          // GRN
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_STORES_INCHARGE" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('GRN'),
            path: paths.dashboard.grn.root,
            icon: ICONS.grnBill,
            children: [
              userRole === "ROLE_ADMIN" || "ROLE_ADMIN" || "ROLE_STORES_INCHARGE" || userRole === "ROLE_FIN_ASSISTANT" ? { title: t('grn creation'), path: paths.dashboard.grn.new } : null,
              userRole === "ROLE_MODERATOR" || "ROLE_ADMIN" || "ROLE_STORES_INCHARGE" || userRole === "ROLE_FIN_ASSISTANT" ? { title: t('grn list'), path: paths.dashboard.grn.root } : null,
            ].filter(item => item !== null),
          } : null,

          // Invoice
          userRole === "ROLE_ADMIN" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('Invoice'),
            path: paths.dashboard.erpinvoice.root,
            icon: ICONS.newinvoice,
            children: [
              { title: t('supplier invoice creation'), path: paths.dashboard.erpinvoice.new },
              { title: t('supplier invoice list'), path: paths.dashboard.erpinvoice.root },
            ],
          } : null,

          // Service Invoice
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_STORES_INCHARGE" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('Service Invoice'),
            path: paths.dashboard.serivceInvoice.root,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('Service Invoice'), path: paths.dashboard?.serivceInvoice?.new },
              { title: t('Service Invoice List'), path: paths.dashboard.serivceInvoice.root },
            ],
          } : null,

          // Cost Update
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('cost update'),
            path: paths.dashboard.costUpdate.root,
            icon: ICONS.configuration,
            children: [
              {
                title: t('new cost update'),
                path: paths.dashboard.costUpdate.new,
              },
              {
                title: t('pending cost update list'),
                path: paths.dashboard.costUpdate.pending,
              },
              {
                title: t('finalised cost update list'),
                path: paths.dashboard.costUpdate.final,
              },
              {
                title: t('All cost update list'),
                path: paths.dashboard.costUpdate.root,
              },
              {
                title: t('final cost update'),
                path: paths.dashboard.costUpdate.finalCostUpdate,
              },
              {
                title: t('cost update configuration'),
                path: paths.dashboard.costUpdate.config,
              },
              {
                title: t('Monthend Report'),
                path: paths.dashboard.costUpdate.monthend,
              },
              {
                title: t('Cost Update Report'),
                path: '#/dashboard/menu_level/menu_level_1b',
                children: [
                  {
                    title: t('Cost Update Report List'),
                    path: paths.dashboard.costUpdate.report,
                  },
                ],
              },
            ],
          } : null,

          // Invoice Return
          userRole === "ROLE_ADMIN" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('Invoice Return'),
            path: paths.dashboard.erpinvoiceReturn.root,
            icon: ICONS.newinvoice,
            children: [
              { title: t('invoice Return'), path: paths.dashboard?.erpinvoiceReturn?.new },
              { title: t('invoice Return list'), path: paths.dashboard.erpinvoiceReturn.list },
            ],
          } : null,

          // Stock Transfer
          {
            title: t('Stock Transfer'),
            path: paths.dashboard.multipleStockTransfer.root,
            icon: ICONS.stock,
            children: [
              { title: t('Multiple Stock Transfer'), path: paths.dashboard.multipleStockTransfer.new },
              { title: t('Stock Transfer List'), path: paths.dashboard.multipleStockTransfer.root },
            ].filter(item => item !== null),
          },

          // Stock Replenishment
          {
            title: t('Stock Replenishment'),
            path: paths.dashboard.stockTransferAccept.root,
            icon: ICONS.stockReplenishment,
            children: [
              { title: t('Stock Replenishment'), path: paths.dashboard.stockTransferAccept.root },
            ],
          },

          // Stock Adjustment
          userRole === "ROLE_ADMIN" || userRole === "ROLE_STORES_INCHARGE" ? {
            title: t('Stock Adjusment'),
            path: paths.dashboard.stockAdjustment.root,
            icon: ICONS.stockAdjustment,
            children: [
              { title: t('Stock Adjusment'), path: paths.dashboard.stockAdjustment.new },
            ],
          } : null,

          // Stock Movement
          {
            title: t('stock Movement'),
            path: paths.dashboard.stockReport.root,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('Stock report - Summary'), path: paths.dashboard.stockReport.itemwiseStockMomentReport },
              { title: t('Stock report - Details'), path: paths.dashboard.stockReport.itemwiseStockDetailsMomentReport },
            ],
          },

          // Stock Report
          {
            title: t('stock report'),
            path: paths.dashboard.stockMovement.root,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('Location wise detail stock'), path: paths.dashboard.report.locationwisestockReport },
              { title: t('Group wise'), path: paths.dashboard.report.itemGroupwisestockReport },
              // { title: t('Stock Details Report'), path: paths.dashboard.report.stockDetailsReport },
              { title: t('Selected Location Stock'), path: paths.dashboard.report.locationdetailsStockReport },
              { title: t('Stock Aging Report'), path: paths.dashboard.report.stockAgingReport },
              // { title: t('All Stock Movement Report'), path: paths.dashboard.report.allStockMovementReport }
            ],
          },

        ].filter(item => item !== null),
      },

      // PRODUCTION
      // ----------------------------------------------------------------------
      {
        subheader: userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_CUTTING_SECTION" || userRole === "ROLE_GALA_VALUE" || userRole === "ROLE_EMB_IN_OUT" || userRole === "ROLE_SIDE_DF_QC" || userRole === "ROLE_IRON_PACKING_DELIVERY" || userRole === "ROLE_FIN_ASSISTANT" || userRole === "ROLE_POS" || userRole === "ROLE_STORES_INCHARGE" ? t('Production') : null,
        items: userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_CUTTING_SECTION" || userRole === "ROLE_GALA_VALUE" || userRole === "ROLE_EMB_IN_OUT" || userRole === "ROLE_SIDE_DF_QC" || userRole === "ROLE_IRON_PACKING_DELIVERY" || userRole === "ROLE_FIN_ASSISTANT" || userRole === "ROLE_POS" || userRole === "ROLE_STORES_INCHARGE" ? [

          // Production Planning
          ...(userRole === "ROLE_ADMIN" ? [
            {
              title: t('Production Planning'),
              path: paths.dashboard.manufacturing.root,
              icon: ICONS.manufacturing,
              children: [
                { title: t('Production Capacity'), path: paths.dashboard.manufacturing.new },
                { title: t('Define Delivery & Trial Date'), path: paths.dashboard.manufacturing.masterData },
                { title: t('Plant'), path: paths.dashboard.manufacturing.plantCreate },
                { title: t('Define Production Category'), path: paths.dashboard.productionCategory.new },
                { title: t('Work Center'), path: paths.dashboard.workCenter.new },
                { title: t('OverHead'), path: paths.dashboard.overHead.new },
                { title: t('BOM'), path: paths.dashboard.billOfMaterial.new },
                { title: t('Production order'), path: paths.dashboard.manufacturing.productionOrder },
                {
                  title: t('Definition'),
                  path: '#/dashboard/menu_level/menu_level_1b',
                  children: [
                    {
                      title: t('Create methods'),
                      path: paths.dashboard.manufacturing.productionOrdermethods,
                    },
                  ],
                },
              ],
            },
          ] : []),

          // Production
          (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_CUTTING_SECTION" || userRole === "ROLE_GALA_VALUE" || userRole === "ROLE_EMB_IN_OUT" || userRole === "ROLE_SIDE_DF_QC" || userRole === "ROLE_IRON_PACKING_DELIVERY" || userRole === "ROLE_STORES_INCHARGE") && {
            title: t('Production'),
            path: paths.dashboard.production.root,
            icon: ICONS.manufacturing,
            children: [
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_STORES_INCHARGE") && {
                title: t('Bundle'),
                path: paths?.dashboard?.production?.bundleCreationRoot,
                children: [
                  { title: t('Bundle Creation'), path: paths.dashboard.production.bundleCreation },
                  { title: t('Bundle List'), path: paths.dashboard.production?.bundleAllList },
                ],
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_CUTTING_SECTION") && {
                title: t('Cutting Out'),
                path: paths.dashboard.production.cuttingOut,
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_GALA_VALUE") && {
                title: t('Gala IN'),
                path: paths.dashboard.production.galaIn,
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_EMB_IN_OUT") && {
                title: t('EMB IN'),
                path: paths.dashboard.production.embInOut,
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_EMB_IN_OUT") && {
                title: t('EMB OUT'),
                path: paths.dashboard.production?.embOut,
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_GALA_VALUE") && {
                title: t('Value Addition IN'),
                path: paths.dashboard.production.valueAdditionIn,
                icon: <StarIcon fontSize="small" sx={{ color: 'green', fontSize: '15px' }} />
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_SIDE_DF_QC") && {
                title: t('Side IN'),
                path: paths.dashboard.production.sideIn,
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_SIDE_DF_QC") && {
                title: t('Down Fall'),
                path: paths.dashboard.production?.downFallIronCreation,
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_SIDE_DF_QC") && {
                title: t('QC IN'),
                path: paths.dashboard.production.qualityCheckIn,
                icon: <StarIcon fontSize="small" sx={{ color: 'green', fontSize: '15px' }} />
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_IRON_PACKING_DELIVERY") && {
                title: t('Iron In'),
                path: paths.dashboard.production?.ironIn,
                children: [
                  { title: t('Steam Iron'), path: paths.dashboard.production?.ironInSteamIron },
                  { title: t('Hevy Iron'), path: paths.dashboard.production?.ironInHeavyIron },
                ],
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_IRON_PACKING_DELIVERY") && {
                title: t('Packing OUT'),
                path: paths.dashboard.production?.parckingOutCreation,
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_IRON_PACKING_DELIVERY") && {
                title: t('Ready for Delivery'),
                path: paths?.dashboard?.production?.finalBundleTransferRoot,
                children: [
                  { title: t('Deliver to branch'), path: paths.dashboard.production?.finalBundleTransfer },
                  { title: t('Ready to Deliver List'), path: paths.dashboard.production?.finalBundleTrasnferList },
                ],
              },
              (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_IRON_PACKING_DELIVERY") && {
                title: t('Delivery Note'),
                path: paths?.dashboard?.production?.boutiqueDeliveryNote,
                children: [
                  { title: t('Delivery Note Create'), path: paths.dashboard.production?.boutiqueDeliveryNoteCreate },
                  { title: t('Delivery Note List'), path: paths.dashboard.production?.boutiqueDeliveryNoteList },
                ],
              },
            ]?.filter(Boolean),
          },

          // Production Manual Entry
          (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_CUTTING_SECTION" || userRole === "ROLE_GALA_VALUE" || userRole === "ROLE_EMB_IN_OUT" || userRole === "ROLE_SIDE_DF_QC" || userRole === "ROLE_IRON_PACKING_DELIVERY") && {
            title: t('Production Manual Entry'),
            path: paths.dashboard.productionManualEntry,
            icon: ICONS.manufacturing,
          },

          // Common Piece Rate
          (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_CUTTING_SECTION" || userRole === "ROLE_GALA_VALUE" || userRole === "ROLE_EMB_IN_OUT" || userRole === "ROLE_SIDE_DF_QC" || userRole === "ROLE_IRON_PACKING_DELIVERY") && {
            title: t('Common Piece Rate'),
            path: paths.dashboard.commonpiecerate.root,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('Common Piece Rate Table'), path: paths.dashboard.commonpiecerate.root },
            ],
          },

          // Production Master
          (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_CUTTING_SECTION" || userRole === "ROLE_GALA_VALUE" || userRole === "ROLE_EMB_IN_OUT" || userRole === "ROLE_SIDE_DF_QC" || userRole === "ROLE_IRON_PACKING_DELIVERY") && {
            title: t('Production Master'),
            path: paths.dashboard?.productionMasters?.root,
            icon: ICONS.stock,
            children: [
              {
                title: t('Cutting Out'),
                path: paths.dashboard?.productionMasters?.cuttingOutRate,
                children: [
                  {
                    title: t('Cutting Out Material Rate'),
                    path: paths.dashboard?.productionMasters?.empAttachedMaterialRateCreate
                  },
                  {
                    title: t('Employee Attached Machine'),
                    path: paths.dashboard?.productionMasters?.empAttachedMachineCreate
                  },
                ]
              },
              {
                title: t('Gala In'),
                path: paths.dashboard?.productionMasters?.galaINRate,
                children: [
                  {
                    title: t('Gala In Material Rate'),
                    path: paths.dashboard?.productionMasters?.galaInMaterialManagemenetCreate
                  },
                  {
                    title: t('Gala In Funda Rate'),
                    path: paths.dashboard?.productionMasters?.galaInFundaRateManagemenetCreate
                  }
                ]
              },
              {
                title: t('Manual Entry'),
                path: paths.dashboard.productionMasters?.manualEntry,
                children: [
                  { title: t('Manual Entry Create'), path: paths.dashboard?.productionMasters?.manualEntryManagemenetCreate },
                ]
              },
              {
                title: t('Value Additon'),
                path: paths.dashboard?.productionMasters?.valueAdditionIn,
                children: [
                  {
                    title: t('Value Addition Gala In '),
                    path: paths.dashboard?.productionMasters?.valueAdditionGalaInRateCreate
                  },
                  {
                    title: t('Value Addition EMB OUT'),
                    path: paths.dashboard?.productionMasters?.valueAddtionalEMBOUTRateCreate
                  },
                  {
                    title: t('Value Addition Common'),
                    path: paths.dashboard?.productionMasters?.valueAdditionCommonRateCreate
                  },
                  {
                    title: t('Value Addition Funda Rate'),
                    path: paths.dashboard?.productionMasters?.valueAdditionCommonFundaRateCreate
                  },
                  {
                    title: t('Value Addition Design Rate'),
                    path: paths.dashboard?.productionMasters?.valueAdditionDesignRateCreate
                  }
                ]
              },
              {
                title: t('Side In'),
                path: paths.dashboard?.productionMasters?.sideINRate,
                children: [
                  {
                    title: t('Side In Material Rate'),
                    path: paths.dashboard?.productionMasters?.sideInMaterialManagemenetCreate
                  },
                  {
                    title: t('Side In Funda Rate'),
                    path: paths.dashboard?.productionMasters?.sideInFundaRateManagemenetCreate
                  }
                ]
              },
              {
                title: t('Iron In'),
                path: paths.dashboard?.productionMasters?.ironInRate,
                children: [
                  {
                    title: t('Iron In Material Rate'),
                    path: paths.dashboard?.productionMasters?.ironInMaterialManagemenetCreate
                  },
                ]
              }
            ]
          },

          // Production Detail Report
          (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_CUTTING_SECTION" || userRole === "ROLE_GALA_VALUE" || userRole === "ROLE_EMB_IN_OUT" || userRole === "ROLE_SIDE_DF_QC" || userRole === "ROLE_IRON_PACKING_DELIVERY") && {
            title: t('Production Detail Report'),
            path: paths.dashboard.productionreport.root,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('Production Detail Report'), path: paths.dashboard.productionreport.newproductiondetails },
              { title: t('Production Detail Summary Report'), path: paths.dashboard.productionreport.newproductiondetailss },
            ],
          },

          // Date wise production
          (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_CUTTING_SECTION" || userRole === "ROLE_GALA_VALUE" || userRole === "ROLE_EMB_IN_OUT" || userRole === "ROLE_SIDE_DF_QC" || userRole === "ROLE_IRON_PACKING_DELIVERY") && {
            title: t('Date wise Production Summary'),
            path: paths.dashboard.datewiseproductionsummery.root,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('Section wise production Summary'), path: paths.dashboard.datewiseproductionsummery.sectionwiseproductionSummery },
              { title: t('Employee wise production summary'), path: paths.dashboard.datewiseproductionsummery.employeewiseproductionSummeru },
            ],
          },

          // Department wise WIP
          (userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_CUTTING_SECTION" || userRole === "ROLE_GALA_VALUE" || userRole === "ROLE_EMB_IN_OUT" || userRole === "ROLE_SIDE_DF_QC" || userRole === "ROLE_IRON_PACKING_DELIVERY") && {
            title: t('Department wise WIP Detail Report'),
            path: paths.dashboard.departmentwisewipdetailsreport.root,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('Department Detailed WIP Report'), path: paths.dashboard.departmentwisewipdetailsreport.departmentdetailedwipreport },
              { title: t('Department Detailed Summary Report'), path: paths.dashboard.departmentwisewipdetailsreport.departmentdetailedwisesummarypreport },
            ],
          },

          // Measurement Issue Report
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('Measurement Issue Report'),
            path: paths.dashboard.measurementAnalysis.root,
            icon: ICONS.setting,
            children: [
              { title: t('Measurement Issue Report'), path: paths.dashboard.measurementAnalysis.root },
            ],
          } : null,

          // Invoice Tracking
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_FIN_ASSISTANT" || userRole === "ROLE_POS" || userRole === "ROLE_STORES_INCHARGE" || userRole === "ROLE_GALA_VALUE" || userRole === "ROLE_CUTTING_SECTION" || userRole === "ROLE_EMB_IN_OUT" || userRole === "ROLE_SIDE_DF_QC" || userRole === "ROLE_IRON_PACKING_DELIVERY" ? {
            title: t('Inovice Tracking'),
            path: paths.dashboard?.bundle?.root,
            icon: ICONS.stock,
            children: [
              {
                title: t('Inovice tracking view'),
                path: paths.dashboard?.bundle?.FinalBundleReceived,
              },
            ]
          } : null,

        ].filter(Boolean) : [],
      },

      // FINANCE
      // ----------------------------------------------------------------------
      {
        subheader: userRole === "ROLE_ADMIN" || userRole === "ROLE_FIN_ASSISTANT" ? t('Finance') : null,
        items: userRole === "ROLE_ADMIN" || userRole === "ROLE_FIN_ASSISTANT" ? [

          // Business Partners
          userRole === "ROLE_ADMIN" ? {
            title: t('Business Partners'),
            path: paths.dashboard.vendor.root,
            icon: ICONS.user,
            children: [
              { title: t('Business Partners creation '), path: paths.dashboard.vendor.new },
              { title: t('Business Partners list'), path: paths.dashboard.vendor.list },
            ],
          } : null,

          // Bank Payment
          userRole === "ROLE_ADMIN" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('Bank Payment'),
            path: paths.dashboard.cashandbankpayment.root,
            icon: ICONS.bank,
            children: [
              { title: t('Payment Voucher'), path: paths.dashboard.cashandbankpayment.new },
              { title: t('Payment Voucher list'), path: paths.dashboard.cashandbankpayment.root },
            ],
          } : null,

          // Petty Cash Payment
          userRole === "ROLE_ADMIN" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('Petty Cash'),
            path: paths.dashboard.cashandbank.root,
            icon: ICONS.pay,
            children: [
              { title: t('Petty Cash Payment'), path: paths.dashboard.cashandbank.new },
              { title: t('Petty Cash Payment list'), path: paths.dashboard.cashandbank.root },
            ],
          } : null,

          // Cash/ Bank Receipt
          userRole === "ROLE_ADMIN" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('Cash / Bank Receipts'),
            path: paths.dashboard.cashandbankReceipts.new,
            icon: ICONS.bankReceipt,
            children: [
              { title: t('Cash / Bank Receipts'), path: paths.dashboard.cashandbankReceipts.new },
              { title: t('Cash / Bank Receipts list'), path: paths.dashboard.cashandbankReceipts.root },
            ],
          } : null,

          // Bank Receipt- Previous
          userRole === "ROLE_ADMIN" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('Bank Receipts - Previous'),
            path: paths.dashboard.bankReceipts.root,
            icon: ICONS.bankReceipt,
            children: [
              { title: t('Bank Receipts list'), path: paths.dashboard.bankReceipts.root },
            ],
          } : null,

          // Journal Entry
          {
            title: t('Journal Entry'),
            path: paths.dashboard.journalEntry.root,
            icon: ICONS.configuration,
            children: [
              {
                title: t('Journal Entry'),
                path: paths.dashboard.journalEntry.new,
              },
            ],
          },

          // Currency Management
          userRole === "ROLE_ADMIN" ? {
            title: t('Currency Management'),
            path: paths.dashboard.currencyManagement.root,
            icon: ICONS.setting,
            children: [
              { title: t('Create Currency'), path: paths.dashboard.currencyManagement.new },
              { title: t('Configure Currency'), path: paths.dashboard.currencyManagement.configure },
            ],
          } : null,

          // Account Payable
          {
            title: t('account Payble'),
            path: paths.dashboard.accountPayble.root,
            icon: ICONS.payable,
            children: [
              { title: t('A/P list'), path: paths.dashboard.accountPayble.root },
            ],
          },

          // Account Receivable
          {
            title: t('account Receivable'),
            path: paths.dashboard.accountReceivable.root,
            icon: ICONS.receable,
            children: [
              // { title: t('create'), path: paths.dashboard.creditMan.new },
              { title: t('A/R list'), path: paths.dashboard.accountReceivable.root },
              // { title: t('details'), path: paths.dashboard.creditMan.demo.details },
              // { title: t('edit'), path: paths.dashboard.creditMan.demo.edit },
            ],
          },

          // Cheque Details
          {
            title: t('cheque details'),
            path: paths.dashboard.postDatedChequeDetails.root,
            icon: ICONS.cheque,
            children: [
              // { title: t('create'), path: paths.dashboard.creditMan.new },
              {
                title: t('Post Dated Receivable'),
                path: paths.dashboard.postDatedChequeDetails.root,
              },
              { title: t('Post Dated Payble'), path: paths.dashboard.postDatedPayble.root },
              // { title: t('details'), path: paths.dashboard.creditMan.demo.details },
              // { title: t('edit'), path: paths.dashboard.creditMan.demo.edit },
            ],
          },

          // Statement Report
          {
            title: t('statement report'),
            path: paths.dashboard.report.root,
            icon: ICONS.report,
            children: [
              {
                title: t('balance sheet'),
                path: paths.dashboard.report.balancesheet,
              },
              {
                title: t('profit and loss'),
                path: paths.dashboard.pnl.list,
              },
              {
                title: t('Final Acc Posting'),
                path: paths.dashboard.finConfig.accpandl,
              },
              {
                title: t('Configuration'),
                path: '#/dashboard/menu_level/menu_level_1b',
                children: [
                  {
                    title: t('Config P&L and BS'),
                    path: paths.dashboard.finConfig.new,
                  },
                  {
                    title: t('Type Set to the P&L'),
                    path: paths.dashboard.finConfig.setConfig,
                  },
                  {
                    title: t('Acc Group Config'),
                    path: paths.dashboard.finConfig.accGroupConfig,
                  },
                  {
                    title: t('Acc Group Line Update'),
                    path: paths.dashboard.finConfig.accLine,
                  },
                  {
                    title: t('Acc Group Oparation'),
                    path: paths.dashboard.finConfig.accOparation,
                  },
                ],
              },
            ],
          },

          // Summary Report
          {
            title: t('summary report'),
            path: '#/dashboard/menu_level',
            icon: ICONS.reportbar,
            children: [
              {
                title: t('trial balance'),
                path: paths.dashboard.chartofaccounts.trialbalance,
              },
            ],
          },

          // VAT Report
          userRole === "ROLE_ADMIN" || userRole === "ROLE_MODERATOR" || userRole === "ROLE_POS" || userRole === "ROLE_FIN_ASSISTANT" ? {
            title: t('VAT Report'),
            path: paths.dashboard.vatinandOut.root,
            icon: ICONS.purchaseOrder,
            children: [
              { title: t('VAT Report'), path: paths.dashboard.vatinandOut.new },
            ],
          } : null,

        ].filter(Boolean) : [],
      },

      // CONFIGURATION
      // ----------------------------------------------------------------------
      {
        subheader: userRole === "ROLE_ADMIN" ? t('Configuration') : null,
        items: userRole === "ROLE_ADMIN" ? [

          // Chart of Accounts
          {
            title: t('chart of accounts'),
            path: '#/dashboard/menu_level',
            icon: ICONS.configuration,
            children: [
              {
                title: t('create new accounts'),
                path: paths.dashboard.chartofaccounts.createnew,
              },
              {
                title: t('Journal list'),
                path: paths.dashboard.chartofaccounts.accountlist,
              },
            ],
          },

          // Configurations
          {
            title: t('configurations'),
            path: '#/dashboard/menu_level',
            icon: ICONS.setups,
            children: [
              {
                title: t('module setup'),
                path: paths.dashboard.module.new,
              },
              {
                title: t('Period Definition'),
                path: paths.dashboard.periodDefinition.new,
              },
              {
                title: t('CA accounts setup'),
                path: '#/dashboard/menu_level/menu_level_1b',
                children: [
                  {
                    title: t('create chart of accounts'),
                    path: paths.dashboard.chartofaccounts.new,
                  },
                ],
              },
            ],
          },

          // System Master
          {
            title: t('System Master'),
            path: paths.dashboard.systemMasterData.root,
            icon: ICONS.setting,
            children: [
              { title: t('system'), path: paths.dashboard.systemMasterData.new },
              { title: t('system location'), path: paths.dashboard.systemMasterData.adminUser },
              { title: t('UOM'), path: paths.dashboard.systemMasterData.uom },
              { title: t('Honorific Names'), path: paths.dashboard.systemMasterData.personalDefinition },
              { title: t('Cost centers'), path: paths.dashboard.systemMasterData.costCenter },
              { title: t('Sub Accounts Type'), path: paths.dashboard.systemMasterData.subAccounts },
              { title: t('Sub Accounts'), path: paths.dashboard.systemMasterData.subAcc },
              { title: t('Reference'), path: paths.dashboard.systemMasterData.reference },
              { title: t('Fiscal Year'), path: paths.dashboard.systemMasterData.fiscalYear },
              {
                title: t('Numbering System'),
                path: '#/dashboard/menu_level/menu_level_1b',
                children: [
                  {
                    title: t('create new '),
                    path: paths.dashboard.numbering.new,
                  },
                  {
                    title: t('create new type'),
                    path: paths.dashboard.numbering.type,
                  },
                ],
              },
            ],
          },

          // Measurement Master
          {
            title: t('Measurements Master'),
            path: paths.dashboard.measurementMaster.root,
            icon: ICONS.setting,
            children: [
              { title: t('Style'), path: paths.dashboard.measurementMaster.style },
              { title: t('Design Number'), path: paths.dashboard.measurementMaster.designNumber },
              { title: t('Relation'), path: paths.dashboard.measurementMaster.relation },
              { title: t('Fit'), path: paths.dashboard.measurementMaster.fit },
              { title: t('S-SEAM'), path: paths.dashboard.measurementMaster.sSeam },
              { title: t('Funda Length'), path: paths.dashboard.measurementMaster.fundaLength },
              { title: t('Button Type'), path: paths.dashboard.measurementMaster.noOfPkt },
              { title: t('Loop'), path: paths.dashboard.measurementMaster.loop },
              { title: t('Back'), path: paths.dashboard.measurementMaster.back },
              { title: t('Neck Style'), path: paths.dashboard.measurementMaster.neckstyle },
              { title: t('Half'), path: paths.dashboard.measurementMaster.half },
              { title: t('SH Cut'), path: paths.dashboard.measurementMaster.shCut },
              { title: t('Pocket Type'), path: paths.dashboard.measurementMaster.pocketType },
              { title: t('Funda'), path: paths.dashboard.measurementMaster.funda },
              { title: t('D/F Type'), path: paths.dashboard.measurementMaster.dfType },
              { title: t('SH Type'), path: paths.dashboard.measurementMaster.shType },
              { title: t('Kally'), path: paths.dashboard.measurementMaster.kally },
              { title: t('Front'), path: paths.dashboard.measurementMaster.front },
              { title: t('Neck'), path: paths.dashboard.measurementMaster.neck },
              { title: t('Sleeve'), path: paths.dashboard.measurementMaster.sleeve },
              { title: t('Dariz Type'), path: paths.dashboard.measurementMaster.darizType },
            ],
          },

          // System Users
          {
            title: t('system users'),
            path: paths.dashboard.adminUser.root,
            icon: ICONS.admin,
            children: [
              { title: t('New System User'), path: paths.dashboard.adminUser.new },
              { title: t('System User List'), path: paths.dashboard.adminUser.list },
            ],
          },

          // C/R Management
          {
            title: t('C/R Management'),
            path: paths.dashboard.adminUser.root,
            icon: ICONS.setting,
            children: [
              { title: t('Cash Register Open'), path: paths.dashboard.cashRegisterList.list },
            ],
          },

        ].filter(Boolean) : [],
      },

      // Upcoming
      {
        subheader: t(t('Upcoming')),
        items: [
          {
            title: t('blank'),
            path: paths.dashboard.blank,
            icon: ICONS.blank,
          },
        ],
      },
    ].filter(section => section.items && section.items.length > 0),
    [t, userRole]
  );

  return data;
}
